import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateFundMappingList,
  useCreateSourceFund,
  useDeleteSourceFund,
  useGetAutoMatchFunds,
  useUpdateSourceFund
} from '../services/funds.service';
import toast from 'react-hot-toast';
import { reorderList } from './transformations/useDefaultTransformation';
import { useReExtractFunds } from '../services/workflow.services';
import { cloneDeep } from 'lodash';

function filterUniqueByInputFundId(arr) {
  const seen = new Set();
  return arr.filter(item => {
    const id = item.input_fund.id;
    if (seen.has(id)) {
      return false;
    } else {
      seen.add(id);
      return true;
    }
  });
}

function areFundListsEqual(prev = [], next = []) {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;

  return prev.every((item, index) => {
    const nextItem = next[index];

    return item === nextItem || item?.id === nextItem?.id;
  });
}

export default function useFundMapping({
  fund_mappings,
  notMappedSourceFunds = [],
  enabled,
  refetch,
  sheetId,
  setHasChanges,
  isOneFund
}) {
  const createFundMappingList = useCreateFundMappingList();
  const createSourceFund = useCreateSourceFund();
  const updateSourceFund = useUpdateSourceFund();
  const deleteSourceFund = useDeleteSourceFund();
  const [notMappedFunds, setNotMappedFunds] = useState<any[]>([]);
  const [autoNotMappedSourceFunds, setAutoNotMappedSourceFunds] = useState<any[]>([]);
  const initialMappingsRef = useRef(null);
  const previousNotMappedSourceFundsRef = useRef<any[]>([]);
  const [mappings, setMappings] = useState();
  const autoMatchFunds = useGetAutoMatchFunds({
    sheetId,
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (!enabled) return;
    if (!fund_mappings) return;

    const result = {
      inputs: filterUniqueByInputFundId([...fund_mappings]),
      sources: filterUniqueByInputFundId([...fund_mappings])
    };

    initialMappingsRef.current = cloneDeep(result);
    setMappings(result);
  }, [fund_mappings, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const nextNotMappedSourceFunds = Array.isArray(notMappedSourceFunds)
      ? notMappedSourceFunds
      : [];

    if (areFundListsEqual(previousNotMappedSourceFundsRef.current, nextNotMappedSourceFunds)) {
      return;
    }

    previousNotMappedSourceFundsRef.current = nextNotMappedSourceFunds;

    setAutoNotMappedSourceFunds(nextNotMappedSourceFunds);
  }, [enabled, notMappedSourceFunds]);

  const handleDeleteSourceFund = (sourceFundId, fn) => {
    deleteSourceFund.mutate(sourceFundId, {
      onSuccess: () => {
        fn();
      },
      onError: err => {
        toast.error(`Delete source fund ${err?.data?.detail}`);
      }
    });
  };

  const handleUpdateSourceFund = (payload, fn) => {
    const _sources = [...mappings?.sources]?.map(el =>
      el.source_fund?.id === payload?.id
        ? {
            ...el,
            source_fund: {
              ...el.source_fund,

              ...payload?.data
            }
          }
        : el
    );

    setMappings(prev => ({ ...prev, sources: _sources }));

    updateSourceFund.mutate(
      { id: payload?.id, data: payload?.data },
      {
        onSuccess: res => {
          fn(res);
        },
        onError: err => {
          toast.error(`Update source fund ${err?.data?.detail}`);
        }
      }
    );
  };

  const handleCreateSourceFund = (payload, fn) => {
    createSourceFund.mutate(payload, {
      onSuccess: res => {
        fn(res);
      },
      onError: err => {
        toast.error(`Create source fund ${err?.data?.detail}`);
      }
    });
  };

  const handleClearFunds = () => {
    setMappings(prev => {
      const _prev = { ...prev };

      _prev.sources = _prev.sources.map(item => ({
        ...item,
        source_fund: null
      }));

      return _prev;
    });
    //setHasChanges(true);
  };

  const onSelectSourceFund = (item, index) => {
    if (item === null) {
      setNotMappedFunds(prev => [...prev, mappings.sources[index].source_fund]);
    } else {
      setNotMappedFunds(prev => prev.filter(value => value.id !== item.id));
    }

    setMappings(prev => {
      const _prev = { ...prev };

      _prev.sources[index].source_fund = item;

      return _prev;
    });

    //setHasChanges(true);
  };

  const handleDragEnd = result => {
    if (!result.destination) return;

    const sourceIndex = result.source?.index;
    const destinationIndex = result.destination?.index;

    const _newSources = reorderList(mappings?.sources, sourceIndex, destinationIndex);

    setMappings(prev => ({ ...prev, sources: _newSources }));
  };

  const allMappedFundsIds = useMemo(() => {
    const inputFundsIds = mappings?.inputs?.map(input => input?.input_fund?.id) || [];

    const sourceFundsIds =
      mappings?.sources
        ?.filter(item => !!item?.source_fund?.id)
        ?.map(input => input?.source_fund?.id) || [];

    return [...inputFundsIds, ...sourceFundsIds];
  }, [JSON.stringify(mappings)]);

  const onSubmitChildForm = async () => {
    return new Promise((resolve, reject) => {
      const innerForm = document.getElementById('new_fund_form');

      if (innerForm) {
        // Add an event listener to detect when the form is submitted
        const onFormSubmit = e => {
          e.preventDefault();
          innerForm.removeEventListener('submit', onFormSubmit);
          resolve(); // Resolve the promise when form is submitted
        };

        innerForm.addEventListener('submit', onFormSubmit);

        // Dispatch the submit event to trigger the form submission
        innerForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } else {
        resolve(); // Resolve immediately if no inner form is found
      }
    });
  };

  const updateAllFundsMapping = async onSuccess => {
    const payload = mappings?.sources?.map(mapp => ({
      sheet_id: mapp?.sheet_id,
      input_fund_id: mapp?.input_fund?.id,
      source_fund_id: mapp?.source_fund?.id || null
    }));

    // Await the inner form submission
    await onSubmitChildForm();

    createFundMappingList.mutate(
      {
        fund_mappings: payload,
        is_per_fund: isOneFund,
        sheet_id: sheetId
      },
      {
        onSuccess: () => {
          refetch();
          onSuccess();
        },
        onError: err => {
          toast.error(
            typeof err?.data?.detail === 'string'
              ? err?.data?.detail
              : 'Error on updating fund mapping'
          );
        }
      }
    );
  };

  const checkFundIsAlreadyChosen = useCallback(
    id => allMappedFundsIds.includes(id),
    [allMappedFundsIds]
  );

  const reExtractFunds = useReExtractFunds();

  const onReExtractFunds = async () => {
    const res = await autoMatchFunds.refetch();
    const fundMappings = Array.isArray(res.data?.fund_mappings) ? res.data?.fund_mappings : [];
    const refreshedNotMapped = Array.isArray(res.data?.not_mapped_source_funds)
      ? res.data?.not_mapped_source_funds
      : [];

    const result = {
      inputs: filterUniqueByInputFundId([...fundMappings]),
      sources: filterUniqueByInputFundId([...fundMappings])
    };

    setMappings(result);
    setAutoNotMappedSourceFunds(refreshedNotMapped);
  };

  return {
    initialMappingsRef,
    mappings,
    isDeleteLoading: deleteSourceFund.isLoading,
    isUpdateLoading: updateSourceFund.isLoading,
    isCreateLoading: createSourceFund.isLoading,
    isUpdateListLoading: createFundMappingList.isLoading,
    isReExtracting: reExtractFunds.isLoading,
    setMappings,
    handleDragEnd,
    onReExtractFunds,
    onSelectSourceFund,
    checkFundIsAlreadyChosen,
    handleCreateSourceFund,
    handleDeleteSourceFund,
    handleUpdateSourceFund,
    updateAllFundsMapping,
    notMappedFunds,
    autoNotMappedSourceFunds,
    isLoadingAutoMatch: autoMatchFunds.isFetching,
    handleClearFunds
  };
}
